import { UserSession, UserSessionClient, UserSessionDevice } from '../model/UserSession'
import { translateClientId } from './keycloakClientLabels.utilities'

const PLACEHOLDER_CLIENT_NAME = /^\$\{[^}]+\}$/

/** Extrait le nom du navigateur depuis une chaîne type "Chrome/146.0.0". */
export const parseBrowserName = (browser?: string | null): string | null => {
  const trimmed = browser?.trim()
  if (!trimmed) {
    return null
  }
  return trimmed.split('/')[0]?.trim() || null
}

/** Libellé principal d'un device, ex. "Chrome sur Linux". */
export const getDeviceLabel = (device: UserSessionDevice): string => {
  const browser =
    parseBrowserName(device.browser) ||
    parseBrowserName(device.sessions.find((session) => session.current)?.browser) ||
    parseBrowserName(device.sessions[0]?.browser) ||
    'Navigateur inconnu'
  const os = device.os?.trim()
  return os ? `${browser} sur ${os}` : browser
}

/** IP affichée pour un device (niveau device ou session la plus pertinente). */
export const getDeviceIpAddress = (device: UserSessionDevice): string | undefined => {
  const deviceIp = device.ipAddress?.trim()
  if (deviceIp) {
    return deviceIp
  }
  const currentSession = device.sessions.find((session) => session.current)
  return currentSession?.ipAddress?.trim() || device.sessions[0]?.ipAddress?.trim()
}

const formatClientLabel = (client: UserSessionClient): string => {
  const name = client.clientName?.trim()
  if (name && !PLACEHOLDER_CLIENT_NAME.test(name)) {
    return name
  }
  return translateClientId(client.clientId)
}

const isUserSessionClient = (value: unknown): value is UserSessionClient =>
  typeof value === 'object' &&
  value !== null &&
  'clientId' in value &&
  typeof (value as UserSessionClient).clientId === 'string'

/** Normalise les clients d'une session en liste de libellés affichables. */
export const getSessionClients = (session: UserSession): string[] => {
  const { clients } = session
  if (!clients) {
    return []
  }

  if (Array.isArray(clients)) {
    return clients
      .map((entry) => {
        if (typeof entry === 'string') {
          return translateClientId(entry.trim())
        }
        if (isUserSessionClient(entry)) {
          return formatClientLabel(entry)
        }
        return ''
      })
      .filter(Boolean)
  }

  return Object.entries(clients)
    .map(([clientId, clientName]) =>
      formatClientLabel({ clientId, clientName: clientName ?? null }),
    )
    .filter(Boolean)
}

/** `true` si l'une des sessions du device est la session courante. */
export const isDeviceCurrent = (device: UserSessionDevice): boolean =>
  device.sessions.some((session) => session.current)

/** Ids de toutes les sessions d'un device. */
export const getDeviceSessionIds = (device: UserSessionDevice): string[] =>
  device.sessions.map((session) => session.id)

/** Ids de toutes les sessions non courantes parmi une liste de devices. */
export const getOtherSessionIds = (devices: UserSessionDevice[]): string[] =>
  devices.flatMap((device) =>
    device.sessions.filter((session) => !session.current).map((session) => session.id),
  )
